"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, PhoneCall, ArrowRight, Zap, Wrench, CircleCheck as CheckCircle2, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  linkText: string;
  linkUrl: string;
}
const faqList: FaqItem[] = [{
  id: "item-1",
  question: "What causes an electric water heater to stop heating?",
  answer: "The most frequent causes include a tripped circuit breaker, a failed upper or lower heating element, a malfunctioning thermostat, or a tripped high-limit safety reset switch. In areas with mineral buildup, sediment accumulation around the heating elements can also cause them to burn out prematurely.",
  linkText: "Schedule Electric Water Heater Repair",
  linkUrl: "/water-heater-repair/"
}, {
  id: "item-2",
  question: "Why is my electric water heater leaking water?",
  answer: "Leaks often originate from a loose drain valve, worn inlet or outlet pipe connections, a faulty temperature and pressure relief (T&P) valve, or internal tank corrosion. If water is leaking directly from the bottom of the tank body due to rust, the tank must be replaced to prevent major flooding.",
  linkText: "Learn about Water Heater Problems",
  linkUrl: "/water-heater-problems/"
}, {
  id: "item-3",
  question: "How much does it cost to run an electric water heater?",
  answer: "Operating costs depend on your local electric utility rates, household water usage, and tank efficiency rating. On average, standard electric tank water heaters account for roughly 15% to 20% of a home's electric bill. Maintaining proper thermostat settings around 120°F helps reduce unnecessary power draw.",
  linkText: "Explore Water Heater Services",
  linkUrl: "/water-heater/"
}, {
  id: "item-4",
  question: "What is the lifespan of an electric water heater?",
  answer: "A standard electric tank water heater generally lasts between 8 and 12 years with routine maintenance, including annual tank flushing and anode rod inspection. Electric tankless models can operate reliably for 15 to 20 years before requiring full unit replacement.",
  linkText: "View Water Heater Replacement Options",
  linkUrl: "/water-heater-replacement/"
}, {
  id: "item-5",
  question: "Should I repair or replace my electric water heater?",
  answer: "If your unit is under 8 years old and the issue involves a replaceable part like an element or thermostat, repair is usually the most cost-effective choice. If the heater is over 10 years old, leaking from the tank shell, or requiring frequent repairs, replacing it is typically more economical.",
  linkText: "Compare Repair vs Replacement",
  linkUrl: "/water-heater-repair/"
}, {
  id: "item-6",
  question: "What are the advantages of a tankless electric water heater?",
  answer: "Tankless electric water heaters heat water on demand rather than keeping a storage tank constantly hot, eliminating standby energy losses. They take up significantly less wall space, provide continuous hot water within their flow capacity, and typically offer a longer operational lifespan.",
  linkText: "Discover Tankless Water Heater Service",
  linkUrl: "/tankless-water-heater-service/"
}];
const quickFacts = [{
  icon: Clock,
  title: "8-12 Year Lifespan",
  detail: "Typical average life for tank units"
}, {
  icon: Zap,
  title: "Thermostat Setting",
  detail: "120°F recommended for safety & efficiency"
}, {
  icon: CheckCircle2,
  title: "Routine Flushing",
  detail: "Annual sediment flushes extend element life"
}];
export default function ElectricHeaterFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="electricwaterheaterfaq" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="space-y-12">
          {/* Section Header */}
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <div className="flex justify-center">
              <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-medium gap-1.5">
                <HelpCircle className="size-3.5 text-primary" />
                Frequently Asked Questions
              </Badge>
            </div>
            <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Electric Water Heater FAQs
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Clear answers to common questions about electric water heater performance, troubleshooting, repair costs, and system replacements in Jonesboro.
            </p>
          </div>

          {/* Highlights Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {quickFacts.map((fact, index) => {
            const IconComponent = fact.icon;
            return <div key={fact.title} data-index={index} className="bg-card text-card-foreground border border-border rounded-xl p-5 shadow-xs flex items-center gap-4 transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm">
                  <div className="p-3 bg-secondary text-secondary-foreground rounded-lg shrink-0">
                    <IconComponent className="size-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-default text-sm font-semibold text-foreground">
                      {fact.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {fact.detail}
                    </p>
                  </div>
                </div>;
          })}
          </div>

          {/* FAQ Accordion Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-8">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-3">
                {faqList.map((faq, index) => <motion.div key={faq.id} variants={itemVariants}>
                    <AccordionItem value={faq.id} data-index={index} className="bg-card text-card-foreground border border-border rounded-xl px-5 py-1 shadow-xs transition-colors data-[state=open]:border-primary/40">
                      <AccordionTrigger className="text-base sm:text-lg font-semibold text-left py-4 hover:no-underline text-foreground group">
                        <span className="flex items-center gap-3">
                          <span className="flex size-7 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-xs font-semibold shrink-0">
                            {index + 1}
                          </span>
                          <span className="group-hover:text-primary transition-colors">
                            {faq.question}
                          </span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 pl-10 pr-2">
                        <p>{faq.answer}</p>
                        <div className="mt-4 pt-3 border-t border-border">
                          <Link to={faq.linkUrl} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-primary hover:underline">
                            <span>{faq.linkText}</span>
                            <ArrowRight className="size-3.5" />
                          </Link>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>)}
              </Accordion>
            </div>

            {/* Sidebar Support Box */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-card text-card-foreground border border-border rounded-xl p-6 shadow-sm space-y-5">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-accent animate-pulse" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Fast Local Dispatch
                    </span>
                  </div>
                  <h3 className="font-default text-xl font-bold text-foreground">
                    Need Direct Assistance?
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    If your electric water heater is leaking, making loud popping sounds, or not producing hot water, our licensed technicians in Jonesboro are available around the clock.
                  </p>
                </div>

                <div className="space-y-3 pt-2">
                  <Button asChild size="lg" className="w-full justify-center gap-2 font-semibold">
                    <Link to="/contact/">
                      <PhoneCall className="size-4" />
                      Request 24/7 Service
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="w-full justify-center gap-2">
                    <Link to="/water-heater-repair/">
                      <Wrench className="size-4" />
                      View Repair Services
                    </Link>
                  </Button>
                </div>

                <div className="pt-4 border-t border-border text-xs text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">Jonesboro Emergency Plumber</p>
                  <p>Serving Jonesboro, GA and surrounding areas 24 hours a day, 7 days a week.</p>
                </div>
              </div>

              <div className="bg-secondary text-secondary-foreground rounded-xl p-5 border border-border/60">
                <h4 className="text-xs font-bold uppercase tracking-wider text-primary">
                  Pro Maintenance Tip
                </h4>
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                  Always shut off the circuit breaker in your electrical panel before inspecting or draining an electric water heater to prevent electrical shocks and dry firing.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}